import {Instant, LocalDate, LocalDateTime, ZonedDateTime, ZoneId} from "@js-joda/core";
import {localStorageOfSerializable} from "../libraries/util/LocalStorage";
import {UserSession} from "../data/auth/UserSession";
import {GlobalEvents, TargetStageChange, UserPreferencesChange} from "./GlobalEvents";
import {UserPreferences} from "../data/preferences/UserPreferences";
import {PaletteMode} from "@mui/material";
import {EnvironmentStage} from "../data/EnvironmentStage";

class GlobalStateClass {
	//////////////////
	// user session //
	//////////////////
	
	private _userSessionStorage = localStorageOfSerializable("userSession", UserSession.deserialize);
	private _userSession: UserSession | null = null;
	
	get userSession(): UserSession | null {
		return this.getUserSessionFromMemory()
			?? this.getUserSessionFromStorage();
	}
	
	set userSession(userSession: UserSession | null) {
		this._userSession = userSession;
		this._userSessionStorage.set(userSession);
	}
	
	get userSessionRequired(): UserSession {
		const userSession = this.userSession;
		
		if (userSession == null)
			throw new Error("No valid user session available.");
		
		return userSession;
	}
	
	private getUserSessionFromStorage() {
		const userSession = this._userSessionStorage.get();
		if (userSession === null)
			return null;
		
		if (GlobalStateClass.isExpired(userSession)) {
			this._userSessionStorage.set(null);
			return null;
		}
		
		// overwrite memory cache
		this._userSession = userSession;
		return userSession;
	}
	
	private getUserSessionFromMemory() {
		const userSession = this._userSession;
		if (userSession === null)
			return null;
		
		if (GlobalStateClass.isExpired(userSession)) {
			this._userSession = null;
			return null;
		}
		
		return userSession;
	}
	
	private static isExpired(userSession: UserSession) {
		return userSession.expiresAt.compareTo(Instant.now()) < 0;
	}
	
	///////////////////
	// user settings //
	///////////////////
	
	private _userPreferencesStorage = localStorageOfSerializable("userPreferences", UserPreferences.deserialize);
	private _userPreferences: UserPreferences | null = null;
	
	get userPreferences(): UserPreferences {
		const userPreferences = this._userPreferences ?? this._userPreferencesStorage.get();
		
		if (userPreferences != null) {
			if (this._userPreferences == null) {
				this._userPreferences = userPreferences;
			}
		}
		
		return userPreferences ?? UserPreferences.default;
	}
	
	set userPreferences(userPreferences: UserPreferences | null) {
		const oldValue = this._userPreferences;
		this._userPreferences = userPreferences;
		this._userPreferencesStorage.set(userPreferences);
		GlobalEvents.userPreferencesChanged.emit(new UserPreferencesChange(
			oldValue ?? UserPreferences.default,
			userPreferences ?? UserPreferences.default
		));
	}
	
	
	//////////////////
	// localization //
	//////////////////
	
	supportedLanguages = UserPreferences.supportedLanguages;
	defaultLanguage = UserPreferences.defaultLanguage;
	
	/**
	 * There are (currently) 4 approaches of displaying a date/time:
	 * - just the date                      for example: 2022-06-24
	 * - date with approximate time (short) for example: 2022-06-24 16:30 (+02:00)
	 * - date with precise time             for example: 2022-06-24 16:30:25 (+02:00)
	 * - date with full time (long)         for example: 2022-06-24 16:30:25.666 (+02:00)
	 *
	 * Each of the above approaches has their own method.
	 * When displaying a temporal value, the developer must choose which amount of information is most suitable for the value.
	 *
	 * For example:
	 * - birthdate: formatDate()
	 * - scheduled party: formatDateTimeShort()
	 * - time of arrival: formatDateTime()
	 * - log timestamp: formatDateTimeLong()
	 */
	
	/**
	 * Formats the given date as string with the following information: year, month, day
	 */
	formatDate(date: LocalDate | string | null | undefined): string {
		if (date === null || date === undefined)
			return "";
		
		const format = this.userPreferences.datePatternOrDefault;
		if (typeof date === "string") {
			const local = LocalDate.parse(date);
			return this.userPreferences.localization.formatDate(local, format);
		} else
			return this.userPreferences.localization.formatDate(date, format);
	}
	
	/**
	 * Formats the given date/time as string with the following information: year, month, day, hour, minute, timezone
	 */
	formatDateTimeShort(dateTime: LocalDateTime | ZonedDateTime | Instant | string | null | undefined): string {
		if (dateTime === null || dateTime === undefined)
			return "";
		
		const format = this.userPreferences.dateTimePatternShortOrDefault;
		if (typeof dateTime === "string") {
			const actualDate = ZonedDateTime.parse(dateTime);
			const local = actualDate.withZoneSameInstant(ZoneId.systemDefault());
			return this.userPreferences.localization.formatDateTime(local, format);
		} else
			return this.userPreferences.localization.formatDateTime(dateTime, format);
	}
	
	/**
	 * Formats the given date/time as string with the following information: year, month, day, hour, minute, second, timezone
	 */
	formatDateTime(dateTime: LocalDateTime | ZonedDateTime | Instant | string | null | undefined): string {
		if (dateTime === null || dateTime === undefined)
			return "";
		
		const format = this.userPreferences.dateTimePatternOrDefault;
		if (typeof dateTime === "string") {
			const actualDate = ZonedDateTime.parse(dateTime);
			const local = actualDate.withZoneSameInstant(ZoneId.systemDefault());
			return this.userPreferences.localization.formatDateTime(local, format);
		} else
			return this.userPreferences.localization.formatDateTime(dateTime, format);
	}
	
	/**
	 * Formats the given date/time as string with the following information: year, month, day, hour, minute, second, millisecond, timezone
	 */
	formatDateTimeLong(dateTime: LocalDateTime | ZonedDateTime | Instant | string | null | undefined): string {
		if (dateTime === null || dateTime === undefined)
			return "";
		
		const format = this.userPreferences.dateTimePatternLongOrDefault;
		if (typeof dateTime === "string") {
			const actualDate = ZonedDateTime.parse(dateTime);
			const local = actualDate.withZoneSameInstant(ZoneId.systemDefault());
			return this.userPreferences.localization.formatDateTime(local, format);
		} else
			return this.userPreferences.localization.formatDateTime(dateTime, format);
	}
	
	paletteMode: PaletteMode = "light";
	
	private _targetStage: EnvironmentStage | null = null;
	
	get targetStage(): EnvironmentStage | null {
		return this._targetStage;
	}
	
	set targetStage(targetStage: EnvironmentStage | null) {
		if (this._targetStage !== targetStage) {
			const oldValue = this._targetStage;
			this._targetStage = targetStage;
			GlobalEvents.targetStageChanged.emit(new TargetStageChange(
				oldValue,
				targetStage
			));
		}
	}
	
	targetKiziStage(defaultStage: EnvironmentStage): string {
		return "kizi" + (this.targetStage ?? defaultStage);
	}
}

export const GlobalState = new GlobalStateClass();
export const formatDate = GlobalState.formatDate.bind(GlobalState);
export const formatDateTimeShort = GlobalState.formatDateTimeShort.bind(GlobalState);
export const formatDateTime = GlobalState.formatDateTime.bind(GlobalState);
export const formatDateTimeLong = GlobalState.formatDateTimeLong.bind(GlobalState);
